import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/config";

const inputCls =
  "w-full bg-ink border border-white/10 focus:border-brand/60 outline-none text-sm text-gray-200 placeholder-gray-600 px-4 py-3 transition-colors";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, register, demoLogin } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success(mode === "login" ? "Welcome back to your site desk." : "Account created — your inventory desk is ready.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const quickDemo = async () => {
    setBusy(true);
    setError("");
    try {
      await demoLogin();
      toast.success("Demo contractor mode active.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-ink min-h-screen pt-16 flex items-center justify-center grain">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        data-testid="auth-modal-container"
        className="w-full max-w-md mx-4 my-12 bg-surface border border-white/10 p-8 relative"
      >
        <div className="absolute -top-3 left-8 bg-brand text-ink font-mono text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1">
          Contractor Access
        </div>

        <div className="flex flex-col items-center text-center mb-8 mt-2">
          <img src={LOGO_URL} alt="Infra Edge" className="h-16 w-16 rounded-full object-cover border border-white/10 mb-4" />
          <h1 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-white">
            {mode === "login" ? "Site Desk Login" : "Create Contractor Account"}
          </h1>
          <p className="font-mono text-xs text-gray-500 mt-2">
            Track cement, steel & aggregate stock across all your sites
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} data-testid="auth-input-name" />
              <input value={form.company} onChange={set("company")} placeholder="Company / firm name" className={inputCls} data-testid="auth-input-company" />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone (WhatsApp)" className={inputCls} data-testid="auth-input-phone" />
            </>
          )}
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="auth-input-email" />
          <input required type="password" minLength={6} value={form.password} onChange={set("password")} placeholder="Password (min 6 chars) *" className={inputCls} data-testid="auth-input-password" />

          {error && (
            <p data-testid="auth-error-message" className="font-mono text-xs text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-btn-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-ink font-heading font-bold uppercase tracking-wide px-6 py-3.5 transition-colors"
          >
            {mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {busy ? "Please wait…" : mode === "login" ? "Login to Dashboard" : "Create Account"}
          </button>
        </form>

        <button
          onClick={quickDemo}
          disabled={busy}
          data-testid="auth-btn-demo-quick-login"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-brand/40 hover:border-brand text-brand font-heading font-bold uppercase tracking-wide px-6 py-3.5 transition-colors text-sm"
        >
          <Zap className="h-4 w-4" /> Explore as Demo Contractor
        </button>

        <p className="mt-6 text-center text-sm text-gray-500">
          {mode === "login" ? "New contractor?" : "Already registered?"}{" "}
          <button
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            data-testid="auth-toggle-mode"
            className="text-brand hover:text-brand-dark font-semibold transition-colors"
          >
            {mode === "login" ? "Create an account" : "Login instead"}
          </button>
        </p>
      </motion.div>
    </main>
  );
}
